import OSS from 'ali-oss';

import request from '@/utils/request';
import { ResponseDTO } from '@/types';

export interface OSSConfig {
  region: string;
  accessKeyId: string;
  accessKeySecret: string;
  stsToken?: string;
  bucket: string;
}

export interface OSSConfigResponse {
  accessKeyId: string;
  accessKeySecret: string;
  bucketName: string;
  expiration: string;
  region: string;
  stsToken: string;
}

export interface UploadProgress {
  progress: number;
  checkpoint?: OSS.Checkpoint;
}

class OSSService {
  private client: OSS | null = null;
  private checkPoint: OSS.Checkpoint | null = null;

  async getOSSUploadConfig(): Promise<OSSConfigResponse> {
    return request
      .get<OSSConfigResponse, ResponseDTO<OSSConfigResponse>, void>('/oss/config')
      .then((res) => res.data as OSSConfigResponse);
  }

  async initialize() {
    const config = await this.getOSSUploadConfig();
    this.client = new OSS({
      region: `oss-${config.region}`,
      authorizationV4: true,
      accessKeyId: config.accessKeyId,
      accessKeySecret: config.accessKeySecret,
      stsToken: config.stsToken,
      bucket: config.bucketName,
      secure: true, // 强制使用HTTPS
      timeout: 6000000, // 超时时间设为600秒
      log: {
        /* eslint-disable-next-line no-console */
        info: console.log,
        /* eslint-disable-next-line no-console */
        error: console.error,
      },
      refreshSTSToken: async () => {
        const refreshConfig = await this.getOSSUploadConfig();
        return {
          accessKeyId: refreshConfig.accessKeyId,
          accessKeySecret: refreshConfig.accessKeySecret,
          stsToken: refreshConfig.stsToken,
        };
      },
    } as OSS.Options);
    return this.client;
  }

  async generateSignatureUrl(fileName: string): Promise<string> {
    try {
      const result: { url: string } = await request.post('/signUrl', {
        url: fileName,
        expires: 3600,
      });
      return result.url;
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.log('error', error);
      throw error;
    }
  }

  async uploadBlob(
    blob: Blob,
    objectName: string,
    onProgress?: (progress: number, abort: () => void) => void,
  ): Promise<{ url: string }> {
    if (!this.client) {
      throw new Error('OSS client not initialized');
    }

    if (this.checkPoint) {
      this.abortUpload();
    }

    const options: OSS.MultipartUploadOptions = {
      progress: (p: number, checkpoint: OSS.Checkpoint) => {
        this.checkPoint = checkpoint;
        // eslint-disable-next-line no-console
        console.log('progress', p, checkpoint);
        if (onProgress) {
          onProgress(p, this.abortUpload.bind(this));
        }
      },
      parallel: 4,
      partSize: 1024 * 1024 * 5, // 5MB per chunk
      mime: blob.type,
      headers: {
        'Content-Type': blob.type,
        'Content-Disposition': 'inline',
      },
    };

    try {
      // 判断如果文件小于10M则使用简单上传
      if (blob.size < 1024 * 1024 * 10) {
        onProgress?.(0, () => {});
        const result = await this.client.put(objectName, blob, {
          headers: {
            'Content-Type': blob.type,
            'Content-Disposition': 'inline',
          },
        });
        onProgress?.(1, () => {});
        return {
          // @ts-expect-error requestUrls is not defined in NormalSuccessResponse
          url: result.url || (result.res.requestUrls?.[0]?.split('?')[0] ?? ''),
        };
      }
      const result = await this.client.multipartUpload(objectName, blob, options);
      this.checkPoint = null;
      return {
        // @ts-expect-error requestUrls is not defined in NormalSuccessResponse
        url: result.res.requestUrls?.[0]?.split('?')[0] ?? '',
      };
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.log('error', error);
      if (error && typeof error === 'object' && 'name' in error && error.name === 'cancel') {
        throw new Error('已取消上传');
      }
      throw error;
    }
  }

  async uploadFile(
    file: File,
    objectName: string,
    onProgress?: (progress: number, abort: () => void) => void,
  ): Promise<{ url: string }> {
    if (!this.client) {
      throw new Error('OSS client not initialized');
    }

    if (this.checkPoint) {
      this.abortUpload();
    }

    const options: OSS.MultipartUploadOptions = {
      progress: (p: number, checkpoint: OSS.Checkpoint) => {
        this.checkPoint = checkpoint;
        // eslint-disable-next-line no-console
        console.log('progress', p, checkpoint);
        if (onProgress) {
          onProgress(p, this.abortUpload.bind(this));
        }
      },
      parallel: 4,
      partSize: 1024 * 1024 * 5, // 5MB per chunk
      mime: file.type,
      headers: {
        'Content-Type': file.type,
        'Content-Disposition': 'inline',
      },
    };

    try {
      // 判断如果文件小于10M则使用简单上传
      if (file.size < 1024 * 1024 * 10) {
        onProgress?.(0, () => {});
        const result = await this.client.put(objectName, file, {
          headers: {
            'Content-Type': file.type,
            'Content-Disposition': 'inline',
          },
        });
        onProgress?.(1, () => {});
        return {
          // @ts-expect-error requestUrls is not defined in NormalSuccessResponse
          url: result.url || (result.res.requestUrls?.[0]?.split('?')[0] ?? ''),
        };
      }
      const result = await this.client.multipartUpload(objectName, file, options);
      this.checkPoint = null;
      return {
        // @ts-expect-error requestUrls is not defined in NormalSuccessResponse
        url: result.res.requestUrls?.[0]?.split('?')[0] ?? '',
      };
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.log('error', error);
      if (error && typeof error === 'object' && 'name' in error && error.name === 'cancel') {
        throw new Error('已取消上传');
      }
      throw error;
    }
  }

  private abortUpload() {
    if (this.client && this.checkPoint) {
      this.client.abortMultipartUpload(this.checkPoint.name, this.checkPoint.uploadId);
    }
  }
}

const oss = new OSSService();

export default oss;
